import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronDown } from 'lucide-react';
import { todayYMD } from '../utils/date';
import { formatPeriodLabel } from '../utils/employeeRoutes';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

function RangeCalendar({
  draftFrom,
  draftTo,
  onSelectDay,
}: {
  draftFrom: string | null;
  draftTo: string | null;
  onSelectDay: (ymd: string) => void;
}) {
  const anchor = draftFrom || draftTo || todayYMD();
  const [viewMonth, setViewMonth] = React.useState(() => {
    const [y, m] = anchor.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, 1);
  });

  // Sync month only while picking the end date (start set, end not yet). Once draftTo
  // is set, keep the current view so cross-month ranges stay visible after the click.
  React.useEffect(() => {
    if (!draftFrom || draftTo) return;
    const [y, m] = draftFrom.slice(0, 10).split('-').map(Number);
    setViewMonth(new Date(y, m - 1, 1));
  }, [draftFrom, draftTo]);

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(startOffset).fill(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const rangeFrom = draftFrom && draftTo ? (draftFrom <= draftTo ? draftFrom : draftTo) : draftFrom;
  const rangeTo = draftFrom && draftTo ? (draftFrom <= draftTo ? draftTo : draftFrom) : null;

  return (
    <>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
          className="rounded p-1 text-sm font-medium text-app-text-subtle hover:bg-app-hover"
          aria-label="Vorheriger Monat"
        >
          ‹
        </button>
        <span className="text-xs font-semibold text-app-text">
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          onClick={() => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
          className="rounded p-1 text-sm font-medium text-app-text-subtle hover:bg-app-hover"
          aria-label="Nächster Monat"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-0.5 text-[10px] font-medium text-app-text-subtle">
            {w}
          </div>
        ))}
        {days.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;
          const ymd = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const inRange =
            rangeFrom && rangeTo ? ymd >= rangeFrom && ymd <= rangeTo : ymd === rangeFrom;
          const isStart = ymd === rangeFrom;
          const isEnd = ymd === rangeTo;
          const isToday = ymd === todayYMD();
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => onSelectDay(ymd)}
              className={[
                'h-7 w-full text-[11px] font-medium transition-colors',
                inRange ? 'bg-pipedrive-blue/15 text-pipedrive-blue' : 'text-app-text-muted hover:bg-app-hover',
                isStart || isEnd ? '!bg-pipedrive-blue !text-white' : '',
                isToday && !isStart && !isEnd ? 'ring-1 ring-pipedrive-blue/40' : '',
                isStart && rangeTo ? 'rounded-l-md' : '',
                isEnd && rangeFrom ? 'rounded-r-md' : '',
                !rangeTo && isStart ? 'rounded-md' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {day}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-app-text-subtle">Start- und Enddatum im Kalender wählen.</p>
    </>
  );
}

export type DateRangeValue = { from: string | null; to: string | null };

type DateRangeFilterDropdownProps = {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  triggerClassName?: string;
  activeLabel?: string;
  inactiveLabel?: string;
};

export function DateRangeFilterDropdown({
  value,
  onChange,
  triggerClassName = '',
  activeLabel,
  inactiveLabel = 'Zeitraum',
}: DateRangeFilterDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [draftFrom, setDraftFrom] = React.useState<string | null>(value.from);
  const [draftTo, setDraftTo] = React.useState<string | null>(value.to);
  const [rangeError, setRangeError] = React.useState<string | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const isActive = Boolean(value.from && value.to);

  React.useEffect(() => {
    if (!open) return;
    setDraftFrom(value.from);
    setDraftTo(value.to);
    setRangeError(null);
  }, [open, value.from, value.to]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const handleSelectDay = (ymd: string) => {
    setRangeError(null);
    if (!draftFrom || (draftFrom && draftTo)) {
      setDraftFrom(ymd);
      setDraftTo(null);
      return;
    }
    if (ymd < draftFrom) {
      setDraftTo(draftFrom);
      setDraftFrom(ymd);
    } else {
      setDraftTo(ymd);
    }
  };

  const apply = () => {
    if (!draftFrom || !draftTo) {
      setRangeError('Bitte Start- und Enddatum wählen.');
      return;
    }
    if (draftFrom > draftTo) {
      setRangeError('Das Startdatum muss vor dem Enddatum liegen.');
      return;
    }
    onChange({ from: draftFrom, to: draftTo });
    setOpen(false);
    setRangeError(null);
  };

  const clear = () => {
    onChange({ from: null, to: null });
    setDraftFrom(null);
    setDraftTo(null);
    setOpen(false);
    setRangeError(null);
  };

  const triggerLabel =
    activeLabel ??
    (isActive && value.from && value.to ? formatPeriodLabel(value.from, value.to) : inactiveLabel);

  const panelWidth = 300;

  const panel =
    open && triggerRef.current ? (
      <div
        ref={panelRef}
        className="fixed z-[200] rounded-lg border border-app-border bg-app-surface p-3 shadow-lg"
        style={{
          top: triggerRef.current.getBoundingClientRect().bottom + 6,
          left: Math.min(
            triggerRef.current.getBoundingClientRect().left,
            window.innerWidth - panelWidth - 12,
          ),
          width: panelWidth,
        }}
        role="dialog"
        aria-label="Zeitraum wählen"
      >
        <RangeCalendar draftFrom={draftFrom} draftTo={draftTo} onSelectDay={handleSelectDay} />
        {rangeError ? <p className="mt-1 text-xs text-red-600">{rangeError}</p> : null}
        <div className="mt-3 grid grid-cols-3 gap-1.5 border-t border-app-border pt-3">
          <button
            type="button"
            onClick={clear}
            className="rounded-md border border-app-border px-1.5 py-1.5 text-[11px] font-medium text-app-text-muted hover:bg-app-hover"
          >
            Zurücksetzen
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md border border-app-border px-1.5 py-1.5 text-[11px] font-medium text-app-text-muted hover:bg-app-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={apply}
            className="rounded-md bg-pipedrive-blue px-1.5 py-1.5 text-[11px] font-semibold text-white hover:opacity-95"
          >
            Übernehmen
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          triggerClassName,
          isActive ? 'text-pipedrive-blue hover:text-pipedrive-blue' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-pressed={isActive}
      >
        <Calendar
          className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-pipedrive-blue' : 'text-app-text-subtle'}`}
          aria-hidden
        />
        <span className="max-w-[140px] truncate">{triggerLabel}</span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 ${isActive ? 'text-pipedrive-blue/70' : 'text-app-text-subtle'}`}
          aria-hidden
        />
      </button>
      {open && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
    </>
  );
}
